import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { supabase } from '../api/supabase'
import type { User, Session } from '@supabase/supabase-js'

interface AuthContextType {
    user: User | null
    session: Session | null
    loading: boolean
    signInWithGoogle: (token: string) => Promise<void>
    signOut: () => Promise<void>
    userRole: string | null
    userRoles: string[]
    userEmail: string | null
    groupId: string | null
    permissionCodes: string[]
    branchCode: string | null
    canViewAllBranches: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<User | null>(null)
    const [session, setSession] = useState<Session | null>(null)
    const [loading, setLoading] = useState(true)
    const [userRole, setUserRole] = useState<string | null>(null)
    const [userRoles, setUserRoles] = useState<string[]>([])
    const [userEmail, setUserEmail] = useState<string | null>(null)
    const [groupId, setGroupId] = useState<string | null>(null)
    const [permissionCodes, setPermissionCodes] = useState<string[]>([])
    const [branchCode, setBranchCode] = useState<string | null>(null)
    const [canViewAllBranches, setCanViewAllBranches] = useState(false)

    // fetch user profile using direct HTTP to avoid Supabase JS client auth-init hang
    const fetchUserRole = async (email: string) => {
        try {
            const stored = JSON.parse(localStorage.getItem('sb-rvpqestlzyidwtoturfe-auth-token') || '{}')
            const token = stored?.access_token
            if (!token) {
                setUserRole(null); setUserRoles([]); setGroupId(null); setPermissionCodes([])
                setBranchCode(null); setCanViewAllBranches(false)
                return
            }
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
            const supabaseAnon = import.meta.env.VITE_SUPABASE_ANON_KEY

            const r = await fetch(
                `${supabaseUrl}/rest/v1/user_roles?user_email=eq.${encodeURIComponent(email)}&select=roles,group_id,branch_code,can_view_all_branches&limit=1`,
                { headers: { 'Authorization': `Bearer ${token}`, 'apikey': supabaseAnon } }
            )
            if (r.ok) {
                const data = await r.json()
                const row = data?.[0]
                const roles: string[] = row?.roles ?? []
                setUserRoles(roles)
                setUserRole(roles[0] ?? null)
                setBranchCode(row?.branch_code ?? null)
                setCanViewAllBranches(row?.can_view_all_branches ?? false)

                const gid: string | null = row?.group_id ?? null
                setGroupId(gid)

                if (gid) {
                    const rp = await fetch(
                        `${supabaseUrl}/rest/v1/group_permissions?group_id=eq.${gid}&select=permission_code`,
                        { headers: { 'Authorization': `Bearer ${token}`, 'apikey': supabaseAnon } }
                    )
                    if (rp.ok) {
                        const perms: { permission_code: string }[] = await rp.json()
                        setPermissionCodes(perms.map(p => p.permission_code))
                    } else {
                        setPermissionCodes([])
                    }
                } else {
                    setPermissionCodes([])
                }
            } else {
                setUserRole(null); setUserRoles([]); setGroupId(null); setPermissionCodes([])
                setBranchCode(null); setCanViewAllBranches(false)
            }
        } catch (error) {
            console.error('Error fetching user role:', error)
            setUserRole(null); setUserRoles([]); setGroupId(null); setPermissionCodes([])
            setBranchCode(null); setCanViewAllBranches(false)
        }
    }

    useEffect(() => {
        const initAuth = async () => {
            try {
                const { data: { session: currentSession } } = await supabase.auth.getSession()
                setSession(currentSession)
                setUser(currentSession?.user ?? null)
                setUserEmail(currentSession?.user?.email ?? null)
                if (currentSession?.user?.email) {
                    await fetchUserRole(currentSession.user.email)
                    Sentry.setUser({
                        id: currentSession.user.id,
                        email: currentSession.user.email,
                        username: currentSession.user.email,
                    })
                } else {
                    Sentry.setUser(null)
                }
            } catch (error) {
                console.error('Error initializing auth:', error)
            } finally {
                setLoading(false)
            }
        }

        initAuth()

        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange(async (_event, newSession) => {
            setSession(newSession)
            setUser(newSession?.user ?? null)
            setUserEmail(newSession?.user?.email ?? null)
            if (newSession?.user?.email) {
                await fetchUserRole(newSession.user.email)
                Sentry.setUser({
                    id: newSession.user.id,
                    email: newSession.user.email,
                    username: newSession.user.email,
                })
            } else {
                setUserRole(null); setUserRoles([]); setGroupId(null); setPermissionCodes([])
                setBranchCode(null); setCanViewAllBranches(false)
                Sentry.setUser(null)
            }
        })

        return () => { subscription?.unsubscribe() }
    }, [])

    const signInWithGoogle = async (token: string) => {
        try {
            const { error } = await supabase.auth.signInWithIdToken({
                provider: 'google',
                token,
            })
            if (error) throw error
        } catch (error) {
            console.error('Error signing in with Google:', error)
            throw error
        }
    }

    const signOut = async () => {
        try {
            const { error } = await supabase.auth.signOut()
            if (error) throw error
            setUser(null); setSession(null); setUserRole(null); setUserRoles([])
            setUserEmail(null); setGroupId(null); setPermissionCodes([])
            setBranchCode(null); setCanViewAllBranches(false)
            Sentry.setUser(null)
        } catch (error) {
            console.error('Error signing out:', error)
            throw error
        }
    }

    return (
        <AuthContext.Provider value={{
            user, session, loading,
            signInWithGoogle, signOut,
            userRole, userRoles, userEmail,
            groupId, permissionCodes,
            branchCode, canViewAllBranches,
        }}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider')
    }
    return context
}
